import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../common';
import { createPortal } from 'react-dom';
import { useClickOutside } from '../../lib/useClickOutside';
import { useSlide } from '../../lib/useSlide';
import { useMediaQuery, PHONE_QUERY } from '../../lib/useMediaQuery';
import { useAccountModal } from '../account/AccountModalProvider';
import { useRecordModal } from './RecordModalProvider';
import { useSearch } from './useSearch';
import { SearchPanel } from './SearchPanel';
import { flattenView, recordKey, MIN_QUERY_LENGTH, type SearchRecord, type SearchScope } from './searchTypes';

// Where a scoped search's "View all" jumps to — the page that lists that type in
// full. Owners + dogs share the Dogs & Owners page. `all` has no single page.
const SCOPE_PAGE: Partial<Record<SearchScope, string>> = {
  owner: '/dogs',
  dog: '/dogs',
  helper: '/rota',
  user: '/account',
};

// AC3 — the ONE global search. Replaces the app-shell stub. Owns the field, the
// panel open/close, keyboard nav (↑/↓/Enter/Esc), and routing a result click to
// the right record modal (booking→edit booking, owner→household, dog→its
// household, helper→edit helper, user→account edit). Data + Recent + debounce
// live in useSearch; the panel is presentational.
export function GlobalSearch({ canSeeUsers }: { canSeeUsers: boolean }) {
  const { q, setQ, scope, setScope, result, loading, recent, addRecent } = useSearch();
  const record = useRecordModal();
  const account = useAccountModal();
  const navigate = useNavigate();
  // The panel is a drawer at the phone tier, so its open/close runs on the same
  // lifecycle as every other drawer in the app: the phase class lands in the
  // same commit as the flag, and it clears on animationend rather than a timer.
  const panel = useSlide(false);
  const open = panel.open;
  const setOpen = panel.setOpen;
  // Opening a record used to CLOSE the panel, so coming back meant retyping the
  // search. It is parked instead: still mounted (query, scope and scroll all
  // intact) and still covering the page behind the record modal, so nothing of
  // the page shows through the change. The modal's back control unparks it.
  const [parked, setParked] = useState(false);
  // Read inside the outside-click handler, which is registered once.
  const parkedRef = useRef(false);
  parkedRef.current = parked;
  // At the phone tier the panel is a full-height sheet, and it cannot be one
  // while it lives inside .topbar: the bar's backdrop-filter makes it the
  // containing block for fixed descendants, so top/bottom resolve against a
  // 42px box and the sheet comes out 2px tall. Portalled to the body instead.
  const phone = useMediaQuery(PHONE_QUERY);
  const [active, setActive] = useState(-1);
  // While parked, every click lands in the record modal — which is "outside"
  // the search shell — so an unguarded outside-close would throw the results
  // away the instant the modal was touched, which is the bug this parking
  // exists to prevent.
  const ref = useClickOutside<HTMLDivElement>(() => {
    if (parkedRef.current) return;
    // The portalled sheet is outside `ref`'s subtree, so without this a tap
    // inside the results would read as "outside" and close them.
    if (portalRef.current?.contains(document.activeElement)) return;
    if (lastPointerInPortal.current) return;
    setOpen(false);
  });
  // The portalled sheet is outside `ref`'s subtree, so a tap inside it would
  // read as "outside" and close the panel the user is using.
  const portalRef = useRef<HTMLDivElement>(null);
  const lastPointerInPortal = useRef(false);
  useEffect(() => {
    const mark = (e: Event) => {
      lastPointerInPortal.current = !!portalRef.current?.contains(e.target as Node);
    };
    document.addEventListener('mousedown', mark, true);
    return () => document.removeEventListener('mousedown', mark, true);
  }, []);
  const inputRef = useRef<HTMLInputElement>(null);

  const term = q.trim();

  // "View all" — only when scoped to a real type with a live query. Jumps to that
  // type's full listing page and closes the panel.
  const destPath = SCOPE_PAGE[scope];
  const viewAll =
    destPath && term.length >= MIN_QUERY_LENGTH
      ? () => {
          navigate(destPath);
          setOpen(false);
        }
      : null;
  // The flat, in-visual-order row list keyboard nav indexes (same order the
  // panel renders).
  const flat = flattenView(term !== '', result, recent);
  const activeKey = active >= 0 && active < flat.length ? recordKey(flat[active]!) : null;

  // Reset the cursor whenever the visible list changes underfoot.
  useEffect(() => {
    setActive(-1);
  }, [q, scope, result, recent]);

  function openRecord(rec: SearchRecord) {
    // How the record modal gets back here. Back leaves the panel exactly as it
    // was found; Dismiss takes it down with the modal.
    const nav = {
      backLabel: 'Results',
      onBack: () => setParked(false),
      onDismiss: () => {
        setParked(false);
        setOpen(false);
      },
    };
    switch (rec.type) {
      case 'owner':
        record?.openOwner(rec.id, nav);
        break;
      case 'dog':
        if (rec.householdId) record?.openOwner(rec.householdId, nav); // dog → its household
        break;
      case 'booking':
        record?.openBooking(rec.id, nav);
        break;
      case 'helper':
        record?.openHelper(rec.id, nav);
        break;
      case 'leave':
        if (rec.householdId) record?.openHelperLeave(rec.householdId, undefined, nav); // leave → the helper's leave view
        break;
      case 'user':
        account?.openUserEdit(rec.id); // id is the email
        break;
    }
    // Add the record's whole cluster to Recent (with context) — found among the
    // current results or the existing Recent list.
    const cluster = [...(result?.clusters ?? []), ...recent].find(
      (c) =>
        recordKey(c.header) === recordKey(rec) || c.members.some((m) => recordKey(m) === recordKey(rec)),
    );
    if (cluster) addRecent(cluster);
    // A user record opens the account modal, which has no nav wiring — close
    // outright there rather than park behind something that cannot unpark it.
    if (rec.type === 'user') setOpen(false);
    else setParked(true);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (flat.length) setActive((i) => (i + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (flat.length) setActive((i) => (i - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      if (active >= 0 && flat[active]) {
        e.preventDefault();
        openRecord(flat[active]!);
      }
    } else if (e.key === 'Escape') {
      if (q) setQ('');
      else setOpen(false);
    }
  }

  // The input is MOVED into the sheet at the phone tier, not copied — which
  // means React unmounts it from the bar and mounts a new one in the portal, and
  // focus does not travel with it. Put it back, or the drawer opens with the
  // keyboard closed and the caret nowhere.
  useEffect(() => {
    if (!phone || !open) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [phone, open]);

  const searchInput = (
    <input
      ref={inputRef}
      className="tb-search-field"
      placeholder="Search owners, dogs, helpers…"
      value={q}
      onChange={(e) => setQ(e.target.value)}
      onFocus={() => setOpen(true)}
      onKeyDown={onKeyDown}
      role="combobox"
      aria-expanded={open}
      aria-controls="gs-panel"
      aria-label="Search"
    />
  );

  return (
    <div
      className={`tb-search gs-shell${term ? ' has-q' : ''}${open ? ' gs-open' : ''}${parked ? ' gs-parked' : ''}`}
      ref={ref}
      // On mobile the field is collapsed to an icon box; tapping it opens the
      // panel + reveals + focuses the field (otherwise the icon is inert).
      onClick={(e) => {
        // A click inside the portalled sheet bubbles up the REACT tree, not the
        // DOM one — so it lands here, on the shell, and used to un-park the
        // results in the same tick that opening a record parked them.
        if (portalRef.current?.contains(e.target as Node)) return;
        setOpen(true);
        setParked(false);
        inputRef.current?.focus();
      }}
    >
      <Icon name="search" size={16} className="tb-search-ico" />
      {/* At the phone tier the field belongs INSIDE the drawer, as the prototype
          has it: full width at the top of the sheet, not a 212px pill wedged in
          the top bar. It is the same element either way — moved, not copied —
          so there is one input and one piece of state. */}
      {!(phone && open) && searchInput}
      {q && (
        <button
          type="button"
          className="icon-btn"
          aria-label="Clear search"
          onClick={() => {
            setQ('');
          }}
        >
          <Icon name="close" size={14} />
        </button>
      )}
      {(open || panel.phase === 'out') &&
        (() => {
          const panelNode = (
            <SearchPanel
              q={q}
              scope={scope}
              result={result}
              loading={loading}
              recent={recent}
              canSeeUsers={canSeeUsers}
              activeKey={activeKey}
              viewAll={viewAll}
              onScope={setScope}
              onOpen={openRecord}
            />
          );
          // Portalled only at the phone tier — on desktop the panel is a
          // dropdown anchored to the field and must stay inside the shell.
          // The wrapper carries the shell's classes so the same selectors
          // (.gs-shell.gs-open, .gs-parked) reach it through the portal.
          return phone
            ? createPortal(
                <div
                  className={
                    `gs-shell gs-portal gs-open${parked ? ' gs-parked' : ''}` +
                    (open ? ' is-open' : '') +
                    (panel.phase === 'in' ? ' phase-in' : panel.phase === 'out' ? ' phase-out' : '')
                  }
                  ref={portalRef}
                  onAnimationEnd={panel.onAnimationEnd}
                >
                  {/* The dim behind the sheet, on the same clock as the sheet. */}
                  <div className="gs-backdrop" onClick={() => setOpen(false)} />
                  <div className="gs-sheet">
                    <div className="gs-grip" aria-hidden="true" />
                    <div className="gs-field-row">
                      <div className="gs-field">
                        <Icon name="search" size={16} />
                        {searchInput}
                        {q && (
                          <button
                            type="button"
                            className="clr"
                            aria-label="Clear search"
                            onClick={() => setQ('')}
                          >
                            <Icon name="close" size={13} />
                          </button>
                        )}
                      </div>
                      <button type="button" className="gs-cancel" onClick={() => setOpen(false)}>
                        Cancel
                      </button>
                    </div>
                    {panelNode}
                  </div>
                </div>,
                document.body,
              )
            : panelNode;
        })()}
    </div>
  );
}
